import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type LimitBillingStatus = 'within_limit' | 'near_limit' | 'over_limit';
export type LimitOverrideKind = 'panel_approval' | 'guarantee_letter';

export interface LimitBillingParty {
  id: string;
  name: string;
}

export interface LimitBillingAuditEntry {
  id: string;
  action: 'near_limit_confirmed' | 'split_applied' | 'override_applied';
  reason: string | null;
  reference: string | null;
  occurredAt: string;
  actor: { id: string; fullName: string };
}

export interface LimitBillingSplit {
  billTotalSen: number;
  eligibleTotalSen: number;
  panelCoverageSen: number;
  patientPaySen: number;
  annualRemainingSen: number | null;
}

export interface LimitBillingAssessment {
  id: string;
  billId: string;
  billNumber: string;
  patient: LimitBillingParty;
  panel: LimitBillingParty;
  status: LimitBillingStatus;
  warningThresholdPercent: number;
  perVisitCapSen: number | null;
  annualCapSen: number | null;
  annualUsedSen: number;
  nearLimitReasons: string[];
  confirmationRequired: boolean;
  confirmedAt: string | null;
  split: LimitBillingSplit;
  override: {
    kind: LimitOverrideKind;
    reference: string;
    reason: string;
    approvedCoverageSen: number;
  } | null;
  audit: LimitBillingAuditEntry[];
}

/**
 * The server re-validates this snapshot in the same transaction that records
 * a payment or creates the claim. Keeping it nested prevents the confirmation
 * from being mistaken for a client-side-only acknowledgement.
 */
export interface LimitAssessmentConfirmation {
  assessmentId: string;
  confirmed: true;
  panelCoverageSen: number;
  patientPaySen: number;
}

export interface ApplyPatientPaySplitInput {
  billId: string;
  assessmentId: string;
  panelCoverageSen: number;
  patientPaySen: number;
}

export interface ApplyLimitOverrideInput {
  billId: string;
  assessmentId: string;
  kind: LimitOverrideKind;
  reference: string;
  reason: string;
  approvedCoverageSen: number;
}

export const limitBillingKeys = {
  all: ['limit-billing'] as const,
  bill: (billId: string) => ['limit-billing', 'bill', billId] as const,
};

export function useLimitBillingAssessment(
  billId: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: limitBillingKeys.bill(billId ?? ''),
    queryFn: ({ signal }) =>
      api
        .get<LimitBillingAssessment>(`/limit-billing/bills/${billId}`, {
          signal,
        })
        .then((response) => response.data),
    enabled: enabled && !!billId,
  });
}

function useAssessmentMutation<TInput extends { billId: string }>(
  mutationFn: (input: TInput) => Promise<LimitBillingAssessment>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (assessment) => {
      queryClient.setQueryData(
        limitBillingKeys.bill(assessment.billId),
        assessment,
      );
      void queryClient.invalidateQueries({ queryKey: ['bills'] });
      void queryClient.invalidateQueries({
        queryKey: ['bill', assessment.billId],
      });
    },
  });
}

export function isStaleLimitAssessmentError(error: unknown) {
  if (!(error instanceof Error) || !('code' in error)) return false;
  const code = (error as { code?: unknown }).code;
  return code === 'LIMIT_ASSESSMENT_STALE' || code === 'LIMIT_SPLIT_CHANGED';
}

export function useApplyLimitOverride() {
  return useAssessmentMutation((input: ApplyLimitOverrideInput) =>
    api
      .post<LimitBillingAssessment>(
        `/limit-billing/bills/${input.billId}/override`,
        {
          assessmentId: input.assessmentId,
          kind: input.kind,
          reference: input.reference,
          reason: input.reason,
          approvedCoverageSen: input.approvedCoverageSen,
        },
      )
      .then((response) => response.data),
  );
}
